/**
 * ShowcaseModal — full-screen showcase used both on the landing page
 * (FeaturedModelModal) and for in-app broadcasts. Header has a tag chip,
 * optional brand icon, big italic title, optional description and an
 * optional pill CTA. Body is a masonry of demo media (delegated to the
 * shared <MediaGallery/> so LongForm popup can reuse the same gallery
 * + lightbox).
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { MediaGallery } from '../MediaGallery';
import type { ShowcaseModalProps } from './types';
import styles from './ShowcaseModal.module.css';

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const ShowcaseModal = ({
  tag,
  title,
  desc,
  brandIcon,
  ctaLabel,
  onCtaClick,
  closeLabel = 'Close',
  photos,
  modelName,
  provider,
  hideLightboxParams = true,
  onTryWithPrompt,
  onClose,
  children,
}: ShowcaseModalProps) => {
  useLockBodyScroll(true);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // ImageModal owns Esc when its lightbox is open. Here we only react
      // to Esc when the lightbox isn't visible — MediaGallery owns its own
      // open state, so detecting that requires either an event or a guard.
      // Existing landing usage closed the showcase on Esc only when the
      // lightbox wasn't open; we keep the same behaviour by always trying
      // close — ImageModal stops propagation when it handles Esc itself.
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const ariaLabelFallback = typeof title === 'string' ? title : undefined;

  return (
    <>
      {createPortal(
        <div className={styles.backdrop} onClick={handleBackdropClick}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="showcase-modal-title"
          >
            <header className={styles.header}>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={onClose}
                aria-label={closeLabel}
              >
                <CloseIcon />
              </button>
              <span className={styles.tag}>{tag}</span>
              <div className={styles.titleRow}>
                {brandIcon && (
                  <img
                    className={styles.brandIcon}
                    src={brandIcon}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                  />
                )}
                <h2 id="showcase-modal-title" className={styles.title}>
                  {title}
                </h2>
              </div>

              {desc && <p className={styles.desc}>{desc}</p>}

              {ctaLabel && onCtaClick && (
                <button type="button" className={styles.cta} onClick={onCtaClick}>
                  <span>{ctaLabel}</span>
                  <ArrowIcon />
                </button>
              )}
            </header>

            <div className={styles.galleryWrap}>
              <MediaGallery
                photos={photos}
                modelName={modelName}
                provider={provider}
                hideLightboxParams={hideLightboxParams}
                onTryWithPrompt={onTryWithPrompt}
                tryLabel={onTryWithPrompt ? ctaLabel : undefined}
                afterTry={onClose}
                ariaLabelFallback={ariaLabelFallback}
              />
            </div>

            {children}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default ShowcaseModal;
